// Phase 6 · rewards (03 §3.8, 15 §15.2). Server-written; a customer reads their own by `customerUid`.
//
//   rewardsAccounts/{customerId}   balance (confirmed, spendable), pending (earned, not yet delivered),
//                                  lifetime (confirmed ever; sets the tier)
//   rewardsLedger/{id}             every change, with its reference; the ids are deterministic so a
//                                  retried trigger or command never earns twice
//   vouchers/{code}                what points were turned into
//   referralCodes/{code}           the owner of a referral code (closed to every client)
//
// The rules themselves are configuration: `rewardsRules/current` (`customerConfig.ts`).
import { z } from 'zod';
import { customerIdSchema } from './cargo';
import { branchCodeSchema, sectorCodeSchema } from './config';
import { customerUidSchema, referralCodeSchema } from './customer';

const iso = z.string();

/** `rewardsAccounts/{customerId}` */
export const rewardsAccountDocSchema = z
  .object({
    customerId: customerIdSchema,
    customerUid: customerUidSchema,
    /** Where staff read it: the customer's branch and country. */
    branchId: branchCodeSchema,
    sectorId: sectorCodeSchema,
    balance: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative(),
    lifetime: z.number().int().nonnegative(),
    tier: z.string(),
    updatedAt: iso,
  })
  .strict();
export type RewardsAccountDoc = z.infer<typeof rewardsAccountDocSchema>;

export const REWARD_ENTRY_TYPES = [
  'earn_pending',
  'confirm',
  'redeem',
  'expire',
  'adjust',
  'referral',
  'review',
  /** Points earned (pending) for a shipment that was cancelled: taken back out of pending. */
  'cancel_pending',
] as const;
export type RewardEntryType = (typeof REWARD_ENTRY_TYPES)[number];

/**
 * `rewardsLedger/{id}`. `points` is the change to the balance, `pendingPoints` the change to the
 * pending total (an `earn_pending` adds to pending; its `confirm` takes the same amount out of pending
 * and puts it in the balance). Entries that add to the balance carry `expiresAt` and `remaining`,
 * which redemptions use oldest first and the daily expiry job clears.
 */
export const rewardsLedgerDocSchema = z
  .object({
    id: z.string(),
    customerId: customerIdSchema,
    customerUid: customerUidSchema,
    branchId: branchCodeSchema,
    sectorId: sectorCodeSchema,
    /** True while `remaining` is above zero (so the entries still holding points can be queried). */
    open: z.boolean().optional(),
    type: z.enum(REWARD_ENTRY_TYPES),
    points: z.number().int(),
    pendingPoints: z.number().int(),
    /** The shipment, voucher or referral the entry is about. */
    reference: z.string().max(80),
    shipmentId: z.string().optional(),
    voucherCode: z.string().optional(),
    /** Why, for an adjustment (required) and for a referral (the friend's first delivery). */
    reason: z.string().max(300).optional(),
    expiresAt: iso.optional(),
    remaining: z.number().int().nonnegative().optional(),
    /** The journal that put the confirmed points in the books (06: Dr 4800, Cr 2020). */
    journalId: z.string().optional(),
    by: z.string(),
    at: iso,
  })
  .strict();
export type RewardsLedgerDoc = z.infer<typeof rewardsLedgerDocSchema>;

/** `vouchers/{code}` */
export const voucherDocSchema = z
  .object({
    code: z.string().regex(/^[A-Z0-9]{8,12}$/),
    customerId: customerIdSchema,
    customerUid: customerUidSchema,
    branchId: branchCodeSchema,
    sectorId: sectorCodeSchema,
    label: z.string(),
    valueMinor: z.number().int().nonnegative().optional(),
    percent: z.number().optional(),
    currency: z.string().optional(),
    /** The least a sale must come to (VAT-inclusive) before the voucher can be used. */
    minSpendMinor: z.number().int().nonnegative().optional(),
    status: z.enum(['issued', 'applied', 'expired']),
    source: z.enum(['points', 'ticket', 'campaign', 'staff']),
    pointsCost: z.number().int().nonnegative().optional(),
    ticketId: z.string().optional(),
    appliedTo: z.string().optional(),
    /** Use at the counter (04 §4.2): when, by whom, and the value it was worth on that sale. */
    appliedAt: iso.optional(),
    appliedBy: z.string().optional(),
    appliedMinor: z.number().int().nonnegative().optional(),
    /** Set when a cancel or refund gave the voucher back. */
    restoredAt: iso.optional(),
    createdAt: iso,
    expiresAt: iso,
  })
  .strict();
export type VoucherDoc = z.infer<typeof voucherDocSchema>;

/** `referralCodes/{code}`: closed to every client. */
export const referralCodeDocSchema = z
  .object({
    code: referralCodeSchema,
    customerId: customerIdSchema,
    customerUid: customerUidSchema,
    createdAt: iso,
  })
  .strict();
